/**
 * Assembly Mode — Class Film fullscreen player.
 *
 * The "zero anxiety" surface for showing a class film in front of a hall
 * of parents. Everything before play is a checklist (sound / screen /
 * film), play itself is one button, controls hide on idle, ESC asks
 * before exiting. End-of-film shows Replay + Back to classroom.
 *
 * Route: /assembly/:workspaceId/:filmId
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Pause, Play, ArrowsOut, ArrowsIn, ArrowClockwise,
         SignOut, SpeakerHigh, SpeakerSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssemblyMode() {
  const { workspaceId, filmId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const rootRef = useRef(null);
  const idleTimerRef = useRef(null);

  const [film, setFilm] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [phase, setPhase] = useState('check'); // check | playing | ended | exiting
  const [checks, setChecks] = useState({ video: false, sound: 'unknown', screen: false });
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // 1. Load the film metadata
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/workspaces/${workspaceId}/films/${filmId}`,
        );
        if (cancelled) return;
        if (data.status !== 'ready' || !data.video_url) {
          setLoadErr('This film isn’t ready to play yet.');
          return;
        }
        setFilm(data);
      } catch (e) {
        if (!cancelled) setLoadErr('Couldn’t load this film.');
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, filmId]);

  // 2. Pre-play safety checks — fullscreen availability + video readiness
  useEffect(() => {
    setChecks((c) => ({
      ...c,
      screen: !!(rootRef.current?.requestFullscreen
                 || rootRef.current?.webkitRequestFullscreen),
    }));
  }, []);

  const onVideoReady = () => setChecks((c) => ({ ...c, video: true }));

  // 3. Begin playback — request fullscreen, unmute, autoplay
  const startAssembly = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      const el = rootRef.current;
      if (el?.requestFullscreen) await el.requestFullscreen();
      else if (el?.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    } catch { /* fullscreen denied — still allow play */ }
    videoRef.current.muted = false;
    setMuted(false);
    try {
      await videoRef.current.play();
      setPlaying(true);
      setPhase('playing');
      setChecks((c) => ({ ...c, sound: 'on' }));
    } catch (e) {
      // Autoplay-with-sound blocked → fall back to muted, user taps to unmute
      videoRef.current.muted = true;
      setMuted(true);
      setChecks((c) => ({ ...c, sound: 'muted' }));
      try {
        await videoRef.current.play();
        setPlaying(true);
        setPhase('playing');
      } catch { /* user must hit play manually */ }
    }
  }, []);

  // 4. Idle-hide controls (mouse/touch resets the 2.5s timer)
  const wakeControls = useCallback(() => {
    setShowControls(true);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setShowControls(false), 2500);
  }, []);
  useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  // 5. ESC handler — show confirm modal instead of exiting straight away
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && phase === 'playing') {
        e.preventDefault();
        setShowExitConfirm(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [phase]);

  // 6. Track exit fullscreen → show confirm
  useEffect(() => {
    const onFs = () => {
      const isFs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      if (!isFs && phase === 'playing') setShowExitConfirm(true);
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, [phase]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const replay = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = 0;
    videoRef.current.play();
    setPhase('playing');
    setPlaying(true);
  };

  const exitAssembly = useCallback(async () => {
    try {
      if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch { /* */ }
    navigate(`/classroom/${workspaceId}`);
  }, [navigate, workspaceId]);

  const onEnded = () => setPhase('ended');

  // ── render ──────────────────────────────────────────────────────────
  if (loadErr) return <FailScreen message={loadErr} onBack={() => navigate(`/classroom/${workspaceId}`)} />;
  if (!film) return <FullscreenSpinner label="Loading…" />;

  return (
    <div
      ref={rootRef}
      data-testid="assembly-root"
      onMouseMove={wakeControls}
      onTouchStart={wakeControls}
      onClick={(e) => {
        // Pre-play: tapping anywhere (except on the explicit Back button)
        // starts assembly. Regains teacher control and feels intentional in
        // the hall. We gate on checks.video so we never start before the
        // video is actually ready to play.
        if (phase !== 'check' || !checks.video) return;
        // Ignore clicks on the explicit cancel/back button so it can still
        // route away — identified by data-testid on the button below.
        const target = e.target.closest && e.target.closest('[data-testid="assembly-cancel"]');
        if (target) return;
        startAssembly();
      }}
      style={{
        background: '#000',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        color: '#fff',
        fontFamily: 'Helvetica, Arial, sans-serif',
        cursor: phase === 'check' && checks.video ? 'pointer' : 'default',
      }}
    >
      <video
        ref={videoRef}
        src={film.video_url}
        playsInline
        preload="auto"
        onCanPlay={onVideoReady}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={onEnded}
        data-testid="assembly-video"
        style={{
          maxHeight: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          background: '#000',
        }}
      />

      {phase === 'check' && (
        <PrePlayChecklist
          film={film}
          checks={checks}
          onStart={startAssembly}
          onCancel={() => navigate(`/classroom/${workspaceId}`)}
        />
      )}

      {phase === 'playing' && (
        <Controls
          playing={playing}
          muted={muted}
          show={showControls}
          onPlayPause={togglePlay}
          onMute={toggleMute}
          onReplay={replay}
          onExit={() => setShowExitConfirm(true)}
        />
      )}

      {phase === 'ended' && (
        <EndScreen
          onReplay={replay}
          onBack={exitAssembly}
        />
      )}

      {showExitConfirm && (
        <ExitConfirm
          onStay={() => setShowExitConfirm(false)}
          onExit={exitAssembly}
        />
      )}
    </div>
  );
}

const PrePlayChecklist = ({ film, checks, onStart, onCancel }) => (
  <div
    data-testid="assembly-checklist"
    style={{
      position: 'absolute',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 18,
      padding: '24px',
    }}
  >
    <div style={{
      fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
      color: '#9A8B6B',
    }}>
      🎬 Assembly Mode
    </div>
    <div style={{
      fontSize: 28, fontWeight: 400,
      fontFamily: "Georgia, 'Times New Roman', serif", textAlign: 'center',
      color: '#fff', maxWidth: 520,
    }}>
      {film.title || 'Your class film'}
    </div>
    {film.subtitle && (
      <div style={{ fontSize: 14, color: '#A8A095' }}>{film.subtitle}</div>
    )}

    <div style={{
      marginTop: 18, display: 'flex', flexDirection: 'column', gap: 8,
      fontSize: 15, color: '#E8E2D5', minWidth: 220,
    }}>
      <CheckRow label="Sound ready" status={checks.sound === 'on' || checks.sound === 'unknown' ? 'pending' : checks.sound} />
      <CheckRow label="Screen ready" status={checks.screen ? 'ok' : 'pending'} />
      <CheckRow label="Film ready"   status={checks.video  ? 'ok' : 'pending'} />
    </div>

    <button
      onClick={onStart}
      disabled={!checks.video}
      data-testid="assembly-start"
      style={{
        marginTop: 20,
        padding: '16px 28px',
        background: checks.video ? '#fff' : '#555',
        color: checks.video ? '#1F1B14' : '#999',
        border: 'none',
        borderRadius: 999,
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '0.04em',
        cursor: checks.video ? 'pointer' : 'not-allowed',
        minWidth: 240,
      }}
    >
      ▶  Start assembly
    </button>
    {checks.video && (
      <div
        data-testid="assembly-tap-hint"
        style={{
          marginTop: 4,
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
          animation: 'assemblyTapPulse 2.4s ease-in-out infinite',
        }}
      >
        Tap anywhere to begin
        <style>{`
          @keyframes assemblyTapPulse {
            0%,100% { opacity: 0.45; }
            50%     { opacity: 0.9;  }
          }
        `}</style>
      </div>
    )}
    <button
      onClick={onCancel}
      data-testid="assembly-cancel"
      style={{
        background: 'none', border: 'none', color: '#A8A095',
        fontSize: 13, marginTop: 4, cursor: 'pointer',
      }}
    >
      Back to classroom
    </button>
    <p style={{
      maxWidth: 360, textAlign: 'center', color: '#7C7568',
      fontSize: 12, marginTop: 12, lineHeight: 1.5,
    }}>
      Designed for school halls — fullscreen, sound on. Press ESC to exit.
    </p>
  </div>
);

const CheckRow = ({ label, status }) => {
  const ok = status === 'ok';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        width: 18, height: 18, borderRadius: 999,
        background: ok ? '#7BB661' : 'transparent',
        border: ok ? 'none' : '1px solid #555',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {ok && <Check size={12} color="#fff" weight="bold" />}
      </span>
      <span style={{ color: ok ? '#E8E2D5' : '#7C7568' }}>{label}</span>
    </div>
  );
};

const Controls = ({ playing, muted, show, onPlayPause, onMute, onReplay, onExit }) => (
  <div
    data-testid="assembly-controls"
    style={{
      position: 'absolute',
      bottom: 28,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: 8,
      padding: '8px 12px',
      background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(10px)',
      borderRadius: 999,
      opacity: show ? 1 : 0,
      transition: 'opacity 200ms ease',
      pointerEvents: show ? 'auto' : 'none',
    }}
  >
    <ControlButton onClick={onPlayPause} title={playing ? 'Pause' : 'Play'}>
      {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
    </ControlButton>
    <ControlButton onClick={onMute} title={muted ? 'Unmute' : 'Mute'}>
      {muted ? <SpeakerSlash size={18} /> : <SpeakerHigh size={18} />}
    </ControlButton>
    <ControlButton onClick={onReplay} title="Replay">
      <ArrowClockwise size={18} />
    </ControlButton>
    <ControlButton onClick={onExit} title="Exit">
      <SignOut size={18} />
    </ControlButton>
  </div>
);

const ControlButton = ({ onClick, title, children }) => (
  <button
    onClick={onClick}
    aria-label={title}
    title={title}
    style={{
      width: 40, height: 40,
      background: 'transparent', border: 'none', color: '#fff',
      cursor: 'pointer', borderRadius: 999,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
  >{children}</button>
);

const EndScreen = ({ onReplay, onBack }) => (
  <div
    data-testid="assembly-end"
    style={{
      position: 'absolute', inset: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 22, color: '#fff',
    }}
  >
    <div style={{
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontSize: 36, fontWeight: 400, color: '#fff',
    }}>
      ✨ Thank you
    </div>
    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
      <button
        onClick={onReplay}
        data-testid="assembly-replay"
        style={{
          padding: '14px 24px', background: '#fff', color: '#1F1B14',
          border: 'none', borderRadius: 999, fontSize: 14, fontWeight: 600,
          cursor: 'pointer', letterSpacing: '0.04em',
        }}
      >Replay</button>
      <button
        onClick={onBack}
        data-testid="assembly-back-to-classroom"
        style={{
          padding: '14px 24px', background: 'transparent', color: '#fff',
          border: '1px solid #555', borderRadius: 999, fontSize: 14, fontWeight: 600,
          cursor: 'pointer', letterSpacing: '0.04em',
        }}
      >Back to classroom</button>
    </div>
  </div>
);

const ExitConfirm = ({ onStay, onExit }) => (
  <div
    data-testid="assembly-exit-confirm"
    style={{
      position: 'absolute', inset: 0,
      background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: 16,
    }}
  >
    <div style={{
      fontFamily: "Georgia, 'Times New Roman', serif",
      fontSize: 24, color: '#fff',
    }}>
      Exit assembly mode?
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <button onClick={onStay}
        data-testid="assembly-stay"
        style={{
          padding: '12px 22px', background: '#fff', color: '#1F1B14',
          border: 'none', borderRadius: 999, fontSize: 14, fontWeight: 600,
          cursor: 'pointer',
        }}
      >Stay</button>
      <button onClick={onExit}
        data-testid="assembly-confirm-exit"
        style={{
          padding: '12px 22px', background: 'transparent', color: '#fff',
          border: '1px solid #555', borderRadius: 999, fontSize: 14, fontWeight: 600,
          cursor: 'pointer',
        }}
      >Exit</button>
    </div>
  </div>
);

const FullscreenSpinner = ({ label }) => (
  <div style={{
    background: '#000', height: '100vh', width: '100vw',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#A8A095', fontFamily: 'Helvetica, Arial, sans-serif',
  }}>{label}</div>
);

const FailScreen = ({ message, onBack }) => (
  <div style={{
    background: '#000', height: '100vh', width: '100vw',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 16,
    color: '#fff', fontFamily: 'Helvetica, Arial, sans-serif',
  }}>
    <div>{message}</div>
    <button
      onClick={onBack}
      data-testid="assembly-fail-back"
      style={{
        padding: '12px 22px', background: '#fff', color: '#1F1B14',
        border: 'none', borderRadius: 999, fontWeight: 600, cursor: 'pointer',
      }}
    >Back to classroom</button>
  </div>
);
